import {
  IconArrowLeft,
  IconLanguage,
  IconMessageCircleStar,
  IconDeviceFloppy,
  IconPencil,
  IconPlus,
  IconArrowNarrowRight,
  IconMapPin,
  IconX,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useTours from "@hooks/tours/useTours";
import { deleteTourRequest } from "@services/tours/toursApi";
import { listAllTourPointsByTourRequest } from "@services/tourPoints/tourPointsApi";
import {
  hydrateTourDraft,
  setTourSelectedLanguages,
} from "@store/slices/tourDraftSlice";
import {
  getTourLanguagesByTourRequest,
  saveTourLanguagesRequest,
} from "@services/tours/tourLanguagesApi";
import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";
import { filterRoutesByTypeAndId } from "@utils/tours/tourRoutes";
import { getTourStatusLabel } from "@utils/tours/tourStatus";
import "@assets/css/maps/MapOverlayPanel.css";
import "@assets/css/mode_tool/panel.css";
import AppModal from "@components/modals/AppModal";
import TourAddForm from "@components/tour/add";
function TourLanguagesModal({
  isOpen,
  onClose,
  languages = [],
  selectedLanguages = [],
  tourId = "",
  tourName = "",
  onSave,
}) {
  const languageList = buildLanguageList(languages);
  const [selectedIso, setSelectedIso] = useState("");
  const [draftLanguages, setDraftLanguages] = useState([]);
  const [loadingTourLanguages, setLoadingTourLanguages] = useState(false);
  const [savingTourLanguages, setSavingTourLanguages] = useState(false);
  const [tourLanguagesError, setTourLanguagesError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSelectedIso("");
      setDraftLanguages([]);
      setTourLanguagesError("");
      return;
    }
    setDraftLanguages(
      Array.isArray(selectedLanguages)
        ? selectedLanguages.map((item) => ({
            id: String(item?.id || ""),
            language_id: String(item?.language_id || ""),
            name: String(item?.name || ""),
            iso: String(item?.iso || "")
              .toLowerCase()
              .trim(),
          }))
        : [],
    );
    setSelectedIso("");
  }, [isOpen, selectedLanguages]);

  useEffect(() => {
    if (!isOpen || !tourId) return;
    let isMounted = true;

    async function fetchTourLanguages() {
      setLoadingTourLanguages(true);
      setTourLanguagesError("");
      try {
        const response = await getTourLanguagesByTourRequest(tourId);
        if (!isMounted) return;
        setDraftLanguages(
          (Array.isArray(response?.body?.languages)
            ? response.body.languages
            : []
          ).map((item) => ({
            id: String(item?.id || ""),
            language_id: String(item?.language_id || ""),
            name: String(item?.name || ""),
            iso: String(item?.iso || "")
              .toLowerCase()
              .trim(),
          })),
        );
      } catch (error) {
        if (!isMounted) return;
        setTourLanguagesError(
          error?.message || "Could not load tour languages.",
        );
      } finally {
        if (isMounted) setLoadingTourLanguages(false);
      }
    }

    fetchTourLanguages();
    return () => {
      isMounted = false;
    };
  }, [isOpen, tourId]);

  const availableLanguages = languageList.filter(
    (language) =>
      !draftLanguages.some(
        (item) =>
          String(item?.iso || "")
            .toLowerCase()
            .trim() === language.iso,
      ),
  );

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Tour languages${tourName ? ` - ${tourName}` : ""}`}
      size="lg"
      className="map-tools-languages-modal-shell"
    >
      <div className="map-tools-languages-modal">
        {loadingTourLanguages ? (
          <p className="map-tools-tour-state">Loading tour languages...</p>
        ) : null}
        {tourLanguagesError ? (
          <p className="map-tools-tour-state map-tools-tour-state-error">
            {tourLanguagesError}
          </p>
        ) : null}
        {!tourId ? (
          <p className="map-tools-tour-state map-tools-tour-state-error">
            Select or open a saved tour first to save languages.
          </p>
        ) : null}

        <div className="tour-add-form-group">
          <p className="tour-add-form-label">languages</p>
          <div className="map-tools-languages-picker">
            <label className="field-label app-form-field">
              <select
                className="field-input select-control"
                value={selectedIso}
                onChange={(event) => setSelectedIso(event.target.value)}
              >
                <option value="">Select language</option>
                {availableLanguages.map((language) => (
                  <option
                    key={language.id || language.iso}
                    value={language.iso}
                  >
                    {language.name || language.iso.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="app-icon-btn app-icon-btn--add"
              onClick={() => {
                const nextIso = String(selectedIso || "")
                  .toLowerCase()
                  .trim();
                if (!nextIso) return;
                const selectedLanguage = languageList.find(
                  (language) => language.iso === nextIso,
                );
                if (!selectedLanguage) return;
                setDraftLanguages((current) => {
                  if (
                    current.some(
                      (item) =>
                        String(item?.iso || "")
                          .toLowerCase()
                          .trim() === nextIso,
                    )
                  ) {
                    return current;
                  }
                  return [
                    ...current,
                    {
                      id: "",
                      language_id: String(selectedLanguage.id || ""),
                      name: String(selectedLanguage.name || ""),
                      iso: nextIso,
                    },
                  ];
                });
                setSelectedIso("");
              }}
              aria-label="Add language to tour"
              title="Add language to tour"
              disabled={
                !selectedIso || loadingTourLanguages || savingTourLanguages
              }
            >
              <IconPlus size={16} />
            </button>
          </div>
        </div>

        <div className="map-tools-languages-list">
          {draftLanguages.map((item) => {
            const iso = String(item?.iso || "")
              .toLowerCase()
              .trim();
            return (
              <article
                key={`${item.id || "new"}-${iso}`}
                className="map-tools-languages-item"
              >
                <strong>{iso.toUpperCase()}</strong>
                <span>{item?.name || iso.toUpperCase()}</span>
                <button
                  type="button"
                  className="app-icon-btn app-icon-btn--delete"
                  onClick={() =>
                    setDraftLanguages((current) =>
                      current.filter(
                        (currentItem) =>
                          String(currentItem?.iso || "")
                            .toLowerCase()
                            .trim() !== iso,
                      ),
                    )
                  }
                  aria-label="Remove language"
                  title="Remove language"
                  disabled={savingTourLanguages}
                >
                  <IconX size={14} />
                </button>
              </article>
            );
          })}
          {!draftLanguages.length ? (
            <p className="map-tools-tour-state">
              Add the languages needed for this tour.
            </p>
          ) : null}
        </div>

        <div className="start-point-promo-save-row">
          <button
            type="button"
            className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${!draftLanguages.length || savingTourLanguages ? "is-disabled" : ""} ${savingTourLanguages ? "is-loading" : ""}`}
            onClick={async () => {
              if (!tourId || !draftLanguages.length) return;
              const payload = {
                tour_id: String(tourId || ""),
                languages: draftLanguages.map((item) => ({
                  id: String(item?.id || ""),
                  language_id: String(item?.language_id || ""),
                  name: String(item?.name || ""),
                  iso: String(item?.iso || "")
                    .toLowerCase()
                    .trim(),
                })),
              };
              console.log("tour languages payload", payload);
              setSavingTourLanguages(true);
              setTourLanguagesError("");
              try {
                const response = await saveTourLanguagesRequest(payload);
                onSave?.(response?.body || payload);
              } catch (error) {
                setTourLanguagesError(
                  error?.message || "Could not save tour languages.",
                );
              } finally {
                setSavingTourLanguages(false);
              }
            }}
            disabled={!draftLanguages.length || savingTourLanguages}
            aria-disabled={!draftLanguages.length || savingTourLanguages}
            aria-label="Save tour languages"
            title="Save tour languages"
          >
            {savingTourLanguages ? (
              <span className="mode-route-tool-btn-spinner" />
            ) : (
              <IconDeviceFloppy size={16} />
            )}
          </button>
        </div>
      </div>
    </AppModal>
  );
}

// Returns plain text from either string translations or { id, value } translations.
function normalizeTranslationValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

// Converts backend translation map into flat { langIso: string } for form editing.
function normalizeTranslationMap(map = {}) {
  return Object.entries(map || {}).reduce((acc, [iso, entry]) => {
    acc[iso] = normalizeTranslationValue(entry);
    return acc;
  }, {});
}

// Extracts translation ids per language from backend shape { id, value }.
function normalizeTranslationIds(map = {}) {
  return Object.entries(map || {}).reduce((acc, [iso, entry]) => {
    if (entry && typeof entry === "object") {
      acc[iso] = entry.id || "";
      return acc;
    }
    acc[iso] = "";
    return acc;
  }, {});
}

function buildLanguageList(languages = []) {
  return languages
    .map((language) => ({
      id: language.lng_id || language.id,
      iso: (language.lng_iso || language.iso || "").toLowerCase(),
      name: language.lng_name || language.name || "",
    }))
    .filter((language) => language.iso);
}

function pickDraftTourName(nameByLang = {}) {
  const en = normalizeTranslationValue(nameByLang.en);
  const es = normalizeTranslationValue(nameByLang.es);
  const pt = normalizeTranslationValue(nameByLang.pt);
  const fallback = normalizeTranslationValue(Object.values(nameByLang)[0]);
  return en || es || pt || fallback || "";
}

// Picks a human-friendly localized tour name from hydrated description map.
function pickTourName(tour) {
  const names = tour?.name || tour?.description?.name || {};
  const en = normalizeTranslationValue(names.en);
  const es = normalizeTranslationValue(names.es);
  const pt = normalizeTranslationValue(names.pt);
  const fallback = normalizeTranslationValue(Object.values(names)[0]);
  return en || es || pt || fallback || "Untitled";
}

// Resolves banner URL from multiple supported response shapes.
function pickTourBannerUrl(tour) {
  const raw =
    tour?.bannerUrl ||
    tour?.banner?.url ||
    tour?.description?.bannerUrl ||
    tour?.description?.banner?.url ||
    "";
  return resolveMediaUrl(raw);
}

// Safe numeric parser for coordinates.
function toFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// Reads first valid coordinate from a list of possible keys.
function readCoordinate(entity, keys) {
  if (!entity) return null;
  for (const key of keys) {
    const value = toFiniteNumber(entity[key]);
    if (value !== null) return value;
  }
  return null;
}

function guessMimeTypeFromUrl(url = "") {
  const value = String(url || "").toLowerCase();
  if (value.endsWith(".mp4")) return "video/mp4";
  if (value.endsWith(".webm")) return "video/webm";
  if (value.endsWith(".ogg")) return "video/ogg";
  if (value.endsWith(".mp3")) return "audio/mpeg";
  if (value.endsWith(".wav")) return "audio/wav";
  if (value.endsWith(".m4a")) return "audio/mp4";
  if (value.endsWith(".aac")) return "audio/aac";
  return "";
}

function normalizePromoVideoMultimedia(tourPromoVideoRaw = {}) {
  if (!tourPromoVideoRaw || typeof tourPromoVideoRaw !== "object") return [];
  const directId =
    tourPromoVideoRaw?.multimediaId || tourPromoVideoRaw?.id || "";
  if (directId) {
    const directUrl = resolveMediaUrl(tourPromoVideoRaw?.url || "");
    return [
      {
        id: directId,
        sourceId: directId,
        url: directUrl,
        mediaType: "video",
        mediaRole: "promoVideo",
        languageIso: "en",
        mimeType: guessMimeTypeFromUrl(directUrl),
      },
    ];
  }

  return Object.entries(tourPromoVideoRaw).reduce((acc, [iso, entry]) => {
    const resolvedIso = String(iso || "").toLowerCase();
    const itemId = entry?.multimediaId || entry?.id || "";
    const itemUrl = resolveMediaUrl(entry?.url || "");
    if (!resolvedIso || !itemId) return acc;
    acc.push({
      id: `${itemId}-${resolvedIso}`,
      sourceId: itemId,
      url: itemUrl,
      mediaType: "video",
      mediaRole: "promoVideo",
      languageIso: resolvedIso,
      mimeType: guessMimeTypeFromUrl(itemUrl),
    });
    return acc;
  }, []);
}

function normalizePromoAudioMultimedia(tourAudioRaw = []) {
  return (tourAudioRaw || []).reduce((acc, item, index) => {
    const audioUrls = item?.audioUrls || {};
    const title = item?.title || {};
    const groupId =
      item?.id || item?.audioGroupId || item?.multimediaId || `audio-${index + 1}`;
    const baseId = item?.multimediaId || item?.id || groupId;
    const entries = Object.entries(audioUrls || {});
    if (!entries.length && (item?.url || "")) {
      acc.push({
        id: `${baseId}-en`,
        sourceId: baseId,
        audioGroupId: groupId,
        url: resolveMediaUrl(item?.url || ""),
        mediaType: "audio",
        mediaRole: "promoAudio",
        languageIso: "en",
        mimeType: guessMimeTypeFromUrl(item?.url || ""),
        audioTitles: title,
      });
      return acc;
    }

    entries.forEach(([iso, url]) => {
      const resolvedIso = String(iso || "").toLowerCase();
      if (!resolvedIso || !url) return;
      const resolvedUrl = resolveMediaUrl(url);
      acc.push({
        id: `${baseId}-${resolvedIso}`,
        sourceId: baseId,
        audioGroupId: groupId,
        url: resolvedUrl,
        mediaType: "audio",
        mediaRole: "promoAudio",
        languageIso: resolvedIso,
        mimeType: guessMimeTypeFromUrl(resolvedUrl),
        audioTitles: {
          [resolvedIso]: title?.[resolvedIso] || "",
        },
      });
    });
    return acc;
  }, []);
}

function resolveTourId(tour) {
  // Supports different id key names returned across environments.
  return tour?.id || tour?.tur_id || tour?.tourId || tour?.code || "";
}

function normalizeStartPoints(tour) {
  // Normalizes  Driving Tour Points from different response nesting formats.
  const candidates = [
    tour?.startPoints,
    tour?.description?.startPoints,
    tour?.description?.start_points,
    tour?.description?.tourStartPoints,
  ];
  const source = candidates.find((item) => Array.isArray(item)) || [];
  return source
    .map((point, index) => {
      const lng = readCoordinate(point, ["lng", "longitude", "lon"]);
      const lat = readCoordinate(point, ["lat", "latitude"]);
      if (lng === null || lat === null) return null;
      return {
        id: point?.id || `start-${index + 1}`,
        internalId:
          point?.internalId ||
          point?.stp_id ||
          point?.stpId ||
          point?.uuid ||
          "",
        isPersisted: true,
        order: Number(point?.order) || index + 1,
        longitude: lng,
        latitude: lat,
        name:
          point?.name ||
          point?.zoneName ||
          point?.placeName ||
          point?.locationName ||
          "",
        status: Number(point?.status) || 1,
        isActive: typeof point?.isActive === "boolean" ? point.isActive : true,
        description: point?.description || "",
        modes: Array.isArray(point?.modes) ? point.modes : [],
        price: {
          currencyIso: point?.price?.currencyIso || point?.currencyIso || "USD",
          amount: Number(point?.price?.amount ?? point?.amount) || 0,
        },
      };
    })
    .filter(Boolean);
}

// Map tools left panel with index list + create/update form.
function MapOverlayPanel({
  isHidden = false,
  isTourFormOpen,
  startPointMode,
  activeStartTour,
  onSelectTourForStartPoints,
  onSelectTourForWalkingPoints,
  onSelectTourForRandomPoints,
  onSelectTourForMapBoundaries,
  onSelectTourForBeforeYouGo,
  onSelectTourForMaximizeExperience,
  onSelectTourForReviews,
  onOpenTourForm,
  isAdminReadOnly = false,
  onBack,
  languages,
  languagesLoading,
  languagesError,
  onLocationPreview,
  onClearLocationPreview,
  onPreviewAllTourRoutes,
  onFetchAllTourPoints,
}) {
  const dispatch = useDispatch();
  const {
    tourId: editingTourId,
    name: editingTourNameByLang,
    selectedLanguages,
  } = useSelector((state) => state.tourDraft);
  const {
    tours,
    loading: toursLoading,
    error: toursError,
    search: toursSearch,
    setSearch: setToursSearch,
    page: toursPage,
    setPage: setToursPage,
    pagination: toursPagination,
    refreshTours,
  } = useTours(10);
  const wasTourFormOpenRef = useRef(isTourFormOpen);
  const tourListRef = useRef(null);
  const tourListScrollTopRef = useRef(0);
  const lastWorkedTourIdRef = useRef("");
  const shouldRestoreTourPositionRef = useRef(false);
  const [deleteTargetTour, setDeleteTargetTour] = useState(null);
  const [deletingTour, setDeletingTour] = useState(false);
  const [deleteTourError, setDeleteTourError] = useState("");
  const [loadingAllPointsByTour, setLoadingAllPointsByTour] = useState({});
  const [isLanguagesModalOpen, setIsLanguagesModalOpen] = useState(false);
  const [selectedTourForLanguages, setSelectedTourForLanguages] =
    useState(null);
  const languageList = buildLanguageList(languages);
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    // Refresh tour index when coming back from form mode.
    if (wasTourFormOpenRef.current && !isTourFormOpen) {
      refreshTours();
    }
    wasTourFormOpenRef.current = isTourFormOpen;
  }, [isTourFormOpen, refreshTours]);

  useLayoutEffect(() => {
    if (isHidden || isTourFormOpen || toursLoading) return;
    if (!shouldRestoreTourPositionRef.current) return;
    const list = tourListRef.current;
    if (!list) return;
    list.scrollTop = tourListScrollTopRef.current;
    const activeTourId = lastWorkedTourIdRef.current;
    const target = activeTourId
      ? Array.from(list.querySelectorAll("[data-tour-id]")).find(
          (item) => String(item?.dataset?.tourId || "") === activeTourId,
        )
      : null;
    if (target) {
      const listRect = list.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      if (targetRect.top < listRect.top || targetRect.bottom > listRect.bottom) {
        target.scrollIntoView({ block: "nearest" });
      }
    }
    shouldRestoreTourPositionRef.current = false;
  }, [isHidden, isTourFormOpen, tours, toursLoading]);

  function rememberTourPosition(tour) {
    lastWorkedTourIdRef.current = String(resolveTourId(tour) || "");
    tourListScrollTopRef.current = Number(tourListRef.current?.scrollTop || 0);
    shouldRestoreTourPositionRef.current = true;
  }

  useEffect(() => {
    if (!languageList.length) {
      setActiveLanguageIso("");
      return;
    }

    setActiveLanguageIso((currentIso) => {
      if (languageList.some((language) => language.iso === currentIso)) {
        return currentIso;
      }
      return languageList[0].iso;
    });
  }, [languageList]);

  useEffect(() => {
    setSearchInput(toursSearch);
  }, [toursSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput === toursSearch) return;
      setToursPage(1);
      setToursSearch(searchInput);
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [searchInput, setToursPage, setToursSearch, toursSearch]);

  useEffect(() => {
    if (!isTourFormOpen || editingTourId || !languageList.length) return;
    if (
      !activeLanguageIso &&
      languageList.some((language) => language.iso === "en")
    ) {
      setActiveLanguageIso("en");
    }
  }, [activeLanguageIso, editingTourId, isTourFormOpen, languageList]);

  const editingTourName = pickDraftTourName(editingTourNameByLang);
  const headerTitle = !isTourFormOpen
    ? `Tours-${tours.length}`
    : editingTourId
      ? editingTourName || " Edit tour"
      : " Add tour";

  // Hydrates draft from selected tour and opens form in update mode.
  function handleEditTour(tour) {
    const description = tour?.description || {};
    const tourImagesRaw = Array.isArray(tour?.tourImages)
      ? tour.tourImages
      : Array.isArray(description?.tourImages)
        ? description.tourImages
        : [];
    const legacyMultimediaRaw = Array.isArray(description?.multimedia)
      ? description.multimedia
      : [];
    const tourAudioRaw = Array.isArray(tour?.tourAudio)
      ? tour.tourAudio
      : Array.isArray(description?.tourAudio)
        ? description.tourAudio
        : [];
    const tourPromoVideoRaw =
      tour?.tourPromoVideo || description?.tourPromoVideo || null;

    const imageMultimedia = tourImagesRaw.map((item) => ({
      id: item?.id || "",
      url: resolveMediaUrl(item?.url || ""),
      mediaType: "image",
      mimeType: item?.mimeType || "image/*",
    }));

    const audioMultimedia = normalizePromoAudioMultimedia(tourAudioRaw);

    const videoMultimedia = normalizePromoVideoMultimedia(tourPromoVideoRaw);

    const legacyMultimedia = legacyMultimediaRaw.map((item) => ({
      id: item?.id || "",
      url: resolveMediaUrl(item?.url || ""),
      mediaType:
        item?.mediaType ||
        item?.mmd_media_type ||
        inferMediaType(item?.url || ""),
      mimeType: item?.mimeType || item?.mmd_mime_type || "",
    }));

    const multimediaMap = new Map();
    [
      ...imageMultimedia,
      ...audioMultimedia,
      ...videoMultimedia,
      ...legacyMultimedia,
    ].forEach((item) => {
      if (!item?.id) return;
      multimediaMap.set(item.id, item);
    });
    const multimedia = Array.from(multimediaMap.values());

    const bannerRaw = tour?.banner || description?.banner || null;
    // Banner can come as id string or object depending on API version.
    const banner =
      bannerRaw && typeof bannerRaw === "object"
        ? {
            id: bannerRaw.id || "",
            url: resolveMediaUrl(bannerRaw.url || ""),
            mediaType: "image",
            mimeType: bannerRaw.mimeType || "",
          }
        : bannerRaw
          ? { id: bannerRaw }
          : null;

    const snapshotRaw =
      tour?.snapshot || description?.snapshot || description?.snapShot || null;
    const snapshot =
      snapshotRaw && typeof snapshotRaw === "object"
        ? {
            id: snapshotRaw.id || "",
            url: resolveMediaUrl(snapshotRaw.url || ""),
            mediaType: "image",
            mimeType: snapshotRaw.mimeType || "",
          }
        : snapshotRaw
          ? { id: snapshotRaw }
          : null;

    dispatch(
      hydrateTourDraft({
        tourId: resolveTourId(tour),
        selectedLanguages: Array.from(
          new Map(
            [
              ...Object.keys(tour?.buttonTitle || tour?.button_title || {}),
              ...Object.keys(tour?.badgeTitle || tour?.badge_title || {}),
              ...Object.keys(tour?.name || description?.name || {}),
              ...Object.keys(
                tour?.description || description?.description || {},
              ),
              ...Object.keys(tour?.duration || description?.duration || {}),
              ...Object.keys(tour?.country || description?.country || {}),
              ...Object.keys(tour?.region || description?.region || {}),
              ...Object.keys(tour?.city || description?.city || {}),
            ]
              .map((iso) =>
                String(iso || "")
                  .toLowerCase()
                  .trim(),
              )
              .filter(Boolean)
              .map((iso) => {
                const catalogLanguage = languageList.find(
                  (language) => language.iso === iso,
                );
                return [
                  iso,
                  {
                    id: "",
                    language_id: String(catalogLanguage?.id || ""),
                    name: String(catalogLanguage?.name || ""),
                    iso,
                  },
                ];
              }),
          ).values(),
        ),
        countryId: tour?.countryId || "",
        countryName: tour?.countryName || "",
        cityId: tour?.cityId || "",
        cityName: tour?.cityName || "",
        status: Number(tour?.status) || 1,
        isActive: typeof tour?.isActive === "boolean" ? tour.isActive : true,
        buttonTitle: normalizeTranslationMap(
          tour?.buttonTitle || tour?.button_title || {},
        ),
        buttonTitleTranslationIds: normalizeTranslationIds(
          tour?.buttonTitle || tour?.button_title || {},
        ),
        badgeTitle: normalizeTranslationMap(
          tour?.badgeTitle || tour?.badge_title || {},
        ),
        badgeTitleTranslationIds: normalizeTranslationIds(
          tour?.badgeTitle || tour?.badge_title || {},
        ),
        name: normalizeTranslationMap(tour?.name || description?.name || {}),
        nameTranslationIds: normalizeTranslationIds(
          tour?.name || description?.name || {},
        ),
        description: normalizeTranslationMap(
          tour?.description || description?.description || {},
        ),
        descriptionTranslationIds: normalizeTranslationIds(
          tour?.description || description?.description || {},
        ),
        duration: normalizeTranslationMap(
          tour?.duration || description?.duration || {},
        ),
        durationTranslationIds: normalizeTranslationIds(
          tour?.duration || description?.duration || {},
        ),
        country: normalizeTranslationMap(
          tour?.country || description?.country || {},
        ),
        countryTranslationIds: normalizeTranslationIds(
          tour?.country || description?.country || {},
        ),
        region: normalizeTranslationMap(
          tour?.region || description?.region || {},
        ),
        regionTranslationIds: normalizeTranslationIds(
          tour?.region || description?.region || {},
        ),
        city: normalizeTranslationMap(tour?.city || description?.city || {}),
        cityTranslationIds: normalizeTranslationIds(
          tour?.city || description?.city || {},
        ),
        pointCount: Number(tour?.pointCount ?? tour?.point_count) || 0,
        stopCount: Number(tour?.stopCount ?? tour?.stop_count) || 0,
        inclusiveAccessType:
          typeof tour?.inclusiveAccessType === "boolean"
            ? tour.inclusiveAccessType
            : typeof tour?.InclussiveAccessType === "boolean"
              ? tour.InclussiveAccessType
              : false,
        multimedia,
        banner,
        snapshot,
      }),
    );
    onOpenTourForm();
  }

  async function handleDeleteTourConfirm() {
    const id = resolveTourId(deleteTargetTour);
    if (!id) return;
    setDeleteTourError("");
    setDeletingTour(true);
    try {
      await deleteTourRequest(id);
      setDeleteTargetTour(null);
      refreshTours();
    } catch (error) {
      const status = Number(error?.status || error?.response?.status || 0);
      if (status === 403) {
        setDeleteTourError("Only super_admin can delete tours.");
      } else if (status === 404) {
        setDeleteTourError("Tour not found.");
      } else if (status === 409) {
        const backendMessage = String(error?.message || "").toLowerCase();
        if (backendMessage.includes("related purchases")) {
          setDeleteTourError("Cannot delete tour: related purchases exist.");
        } else if (
          backendMessage.includes("without related start_tour_prices")
        ) {
          setDeleteTourError(
            "Cannot delete tour: there are  Driving Tour Points without related start_tour_prices.",
          );
        } else {
          setDeleteTourError("Cannot delete tour due to related records.");
        }
      } else {
        setDeleteTourError(error?.message || "Could not delete tour.");
      }
    } finally {
      setDeletingTour(false);
    }
  }

  async function mapOverlayFetchAllTourPoints(
    event,
    tour,
    routeType = "",
    routeId = "",
  ) {
    if (typeof event?.stopPropagation === "function") {
      event.stopPropagation();
    }
    const tourId = resolveTourId(tour);
    if (!tourId) return;
    setLoadingAllPointsByTour((prev) => ({ ...prev, [tourId]: true }));
    try {
      const response = await listAllTourPointsByTourRequest(tourId);
      const payload = response?.body || response || {};
      const routes = filterRoutesByTypeAndId(
        payload?.routes,
        routeType,
        routeId,
      );
      onPreviewAllTourRoutes?.({
        tour,
        tourId,
        routes,
        startPoints: Array.isArray(payload?.start_points)
          ? payload.start_points
          : [],
      });
    } catch (error) {
    } finally {
      setLoadingAllPointsByTour((prev) => ({ ...prev, [tourId]: false }));
    }
  }

  return (
    <div
      className={`map-tools-overlay ${isTourFormOpen ? "add-tour" : ""} ${startPointMode ? "start-point-mode" : ""} ${isHidden ? "is-tour-index-hidden" : ""}`}
      aria-hidden={isHidden}
    >
      {/* Panel header. */}
      <div
        className={
          isTourFormOpen
            ? "mode-route-tool-header map-tools-header--form"
            : "map-tools-header"
        }
      >
        {isTourFormOpen && languageList.length ? (
          <div className="app-language-section">
            <div className="app-language-switcher" aria-label="Languages">
              {languageList.map((language) => (
                <button
                  key={language.id || language.iso}
                  type="button"
                  className={`app-language-btn ${activeLanguageIso === language.iso ? "is-active" : ""}`}
                  onClick={() => setActiveLanguageIso(language.iso)}
                  aria-pressed={activeLanguageIso === language.iso}
                  title={language.name || language.iso.toUpperCase()}
                >
                  {language.iso.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {isTourFormOpen ? (
          <div className="mode-route-tool-head">
            <button
              className="app-icon-btn app-icon-btn--back map-tools-back"
              type="button"
              aria-label="Go back"
              title="Go back"
              onClick={onBack}
            >
              <IconArrowLeft size={16} />
            </button>
            <h3 className="mode-route-tool-title">
              Tour
              {editingTourId && editingTourName ? ` - ${editingTourName}` : ""}
              {/* <p className="mode-route-tool-subtitle">
                {editingTourId ? " update tour" : " add tour"}
              </p> */}
            </h3>
          </div>
        ) : null}
        {!isTourFormOpen ? (
          <h3 className="map-tools-title">{headerTitle}</h3>
        ) : null}

        {/* Main action in index mode. */}
        {!isTourFormOpen && !isAdminReadOnly && (
          <div className="map-tools-add-tour-wrap">
            {/* btn add tour */}
            <button
              className="app-icon-btn app-icon-btn--add map-tools-add-btn"
              type="button"
              aria-label="Add tour"
              title="Add tour"
              onClick={onOpenTourForm}
            >
              <IconPlus size={24} />
            </button>
          </div>
        )}
      </div>

      {!isTourFormOpen ? (
        <>
          <div className="map-tools-tour-shell">
            <div className="map-tools-search-block">
              <label className="field-label app-form-field map-tools-search-field">
                <input
                  className="field-input"
                  value={searchInput}
                  placeholder="Search tours"
                  onChange={(event) => {
                    setSearchInput(event.target.value);
                    if (!String(event.target.value || "").trim()) {
                      setToursPage(1);
                      setToursSearch("");
                    }
                  }}
                />
              </label>
            </div>
            {/* Tour index cards with status/active badges and quick actions. */}
            <div
              ref={tourListRef}
              className="map-tools-tour-list"
              onScroll={(event) => {
                tourListScrollTopRef.current = event.currentTarget.scrollTop;
              }}
            >
              {toursLoading ? (
                <p className="map-tools-tour-state">Loading tours...</p>
              ) : null}
              {toursError ? (
                <p className="map-tools-tour-state map-tools-tour-state-error">
                  {toursError}
                </p>
              ) : null}
              {!toursLoading && !toursError && !tours.length ? (
                <p className="map-tools-tour-state">
                  {String(toursSearch || "").trim()
                    ? "No tours match your search."
                    : "No tours created yet."}
                </p>
              ) : null}
              {!toursLoading && !toursError
                ? tours.map((tour) => {
                    const bannerUrl = pickTourBannerUrl(tour);
                    const bannerId = tour?.description?.banner || "";
                    return (
                      <article
                        key={resolveTourId(tour) || tour.code}
                        className="map-tools-tour-card"
                        data-tour-id={resolveTourId(tour)}
                        onClickCapture={() => rememberTourPosition(tour)}
                      >
                        <div className="map-tools-tour-banner">
                          <div className="map-tools-tour-badges">
                            <span
                              className={`map-tools-badge status-${getTourStatusLabel(tour.status)}`}
                            >
                              {getTourStatusLabel(tour.status)}
                            </span>
                            <span
                              className={`map-tools-badge ${tour.isActive ? "is-active" : "is-inactive"}`}
                            >
                              {tour.isActive ? "active" : "inactive"}
                            </span>
                          </div>
                          {!isAdminReadOnly ? (
                            <button
                            type="button"
                            className="app-icon-btn app-icon-btn--update map-tools-tour-language"
                            aria-label="Manage tour languages"
                            title="Manage tour languages"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedTourForLanguages(tour);
                              setIsLanguagesModalOpen(true);
                            }}
                          >
                            <IconLanguage size={20} />
                          </button>
                          ) : null}
                          {!isAdminReadOnly ? (
                            <button
                            type="button"
                            className="app-icon-btn app-icon-btn--update map-tools-tour-reviews"
                            aria-label="Manage tour reviews"
                            title="Manage tour reviews"
                            onClick={(event) => {
                              event.stopPropagation();
                              onSelectTourForReviews?.({
                                tour,
                                tourId: resolveTourId(tour),
                              });
                            }}
                          >
                            <IconMessageCircleStar size={20} />
                          </button>
                          ) : null}
                          {!isAdminReadOnly ? (
                            <button
                            type="button"
                            className="app-icon-btn app-icon-btn--update map-tools-tour-edit"
                            aria-label="Update tour"
                            title="Update tour"
                            onClick={() => handleEditTour(tour)}
                          >
                            <IconPencil size={20} />
                          </button>
                          ) : null}
                          {/*  <button
                          type="button"
                          className="app-icon-btn app-icon-btn--delete map-tools-tour-delete"
                          aria-label="Delete tour"
                          title="Delete tour"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteTourError("");
                            setDeleteTargetTour(tour);
                          }}
                        >
                          <IconTrash size={18} />
                        </button> */}
                          {bannerUrl ? (
                            <img
                              src={bannerUrl}
                              alt={pickTourName(tour)}
                              role="button"
                              tabIndex={0}
                              disabled={Boolean(
                                loadingAllPointsByTour?.[resolveTourId(tour)],
                              )}
                              onClick={(event) => {
                                const fetchAllTourPoints =
                                  typeof onFetchAllTourPoints === "function"
                                    ? onFetchAllTourPoints
                                    : mapOverlayFetchAllTourPoints;
                                fetchAllTourPoints(event, tour, "", "");
                              }}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  const fetchAllTourPoints =
                                    typeof onFetchAllTourPoints === "function"
                                      ? onFetchAllTourPoints
                                      : mapOverlayFetchAllTourPoints;
                                  fetchAllTourPoints(event, tour, "", "");
                                }
                              }}
                            />
                          ) : (
                            <div
                              className="map-tools-tour-banner-fallback"
                              role="button"
                              tabIndex={0}
                            >
                              {bannerId ? `Banner: ${bannerId}` : "No banner"}
                            </div>
                          )}
                        </div>
                        <div
                          className="map-tools-tour-body"
                          role="button"
                          tabIndex={0}
                        >
                          <strong className="tour-name">
                            {pickTourName(tour)}
                          </strong>
                          <small>
                            <IconMapPin size={20} />
                            {tour.cityName || "No city"} -{" "}
                            {tour.countryName || "No country"}
                          </small>
                          {!isAdminReadOnly ? (
                            <button
                              type="button"
                              className={`button-ghost map-tools-start-btn btn-starting-point ${
                                String(resolveTourId(activeStartTour)) ===
                                String(resolveTourId(tour))
                                  ? "active"
                                  : ""
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                onSelectTourForStartPoints?.({
                                  tour,
                                  tourId: resolveTourId(tour),
                                  startPoints: normalizeStartPoints(tour),
                                });
                              }}
                            >
                              {String(resolveTourId(activeStartTour)) ===
                              String(resolveTourId(tour))
                                ? ""
                                : " Driving Tour Points"}
                              <IconArrowNarrowRight size={24} />
                            </button>
                          ) : null}
                          {/* <small>{tour.code || "Sin codigo"}</small> */}
                          <div className="line-separator"></div>
                          {/*routing types, driving and random  */}
                          {!isAdminReadOnly ? (
                            <button
                              type="button"
                              className="button-ghost map-tools-start-btn btn-walkin-point"
                              onClick={(event) => {
                                event.stopPropagation();
                                onSelectTourForWalkingPoints?.({
                                  tour,
                                  tourId: resolveTourId(tour),
                                });
                              }}
                            >
                              Walking Tour Points
                              <IconArrowNarrowRight size={24} />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="button-ghost map-tools-start-btn btn-random-point"
                            onClick={(event) => {
                              event.stopPropagation();
                              onSelectTourForRandomPoints?.({
                                tour,
                                tourId: resolveTourId(tour),
                              });
                            }}
                          >
                            Points of Interest
                            <IconArrowNarrowRight size={24} />
                          </button>
                          {!isAdminReadOnly ? (
                            <button
                              type="button"
                              className="button-ghost map-tools-start-btn btn-random-point"
                              onClick={(event) => {
                                event.stopPropagation();
                                onSelectTourForMapBoundaries?.({
                                  tour,
                                  tourId: resolveTourId(tour),
                                });
                              }}
                            >
                              Map Boundaries
                              <IconArrowNarrowRight size={24} />
                            </button>
                          ) : null}
                          <div className="line-separator"></div>
                          {/*before you go */}
                          <button
                            type="button"
                            className="button-ghost map-tools-start-btn btn-before-you-go"
                            onClick={(event) => {
                              event.stopPropagation();
                              onSelectTourForBeforeYouGo?.({
                                tour,
                                tourId: resolveTourId(tour),
                              });
                            }}
                          >
                            Before you go
                            <IconArrowNarrowRight size={24} />
                          </button>
                          <button
                            type="button"
                            className="button-ghost map-tools-start-btn btn-maximaze-experience"
                            onClick={(event) => {
                              event.stopPropagation();
                              onSelectTourForMaximizeExperience?.({
                                tour,
                                tourId: resolveTourId(tour),
                              });
                            }}
                          >
                            Maximize Your Experience
                            <IconArrowNarrowRight size={24} />
                          </button>
                        </div>
                      </article>
                    );
                  })
                : null}
            </div>
          </div>
          <div className="map-tools-tour-pagination">
            <button
              type="button"
              className="app-icon-btn app-icon-btn--update"
              onClick={() => setToursPage(Math.max(1, toursPage - 1))}
              disabled={!toursPagination?.hasPreviousPage || toursLoading}
              aria-label="Previous page"
              title="Previous page"
            >
              <IconChevronLeft size={16} />
            </button>
            <p className="map-tools-tour-pagination-text">
              Page {toursPagination?.page || 1} of{" "}
              {toursPagination?.totalPages || 1}
              <span> · {toursPagination?.total || 0} tours</span>
            </p>
            <button
              type="button"
              className="app-icon-btn app-icon-btn--update"
              onClick={() => setToursPage((toursPagination?.page || 1) + 1)}
              disabled={!toursPagination?.hasNextPage || toursLoading}
              aria-label="Next page"
              title="Next page"
            >
              <IconChevronRight size={16} />
            </button>
          </div>
        </>
      ) : null}

      {isTourFormOpen ? (
        <>
          {languagesLoading ? (
            <p className="map-tools-form-state">Loading languages...</p>
          ) : null}
          {languagesError ? (
            <p className="map-tools-form-state map-tools-form-state-error">
              {languagesError}
            </p>
          ) : null}
          {!languagesLoading && !languagesError ? (
            <div className="map-tools-form-panel">
              <TourAddForm
                languages={languages}
                activeLanguageIso={activeLanguageIso}
                onLocationPreview={onLocationPreview}
                onClearLocationPreview={onClearLocationPreview}
                onDone={onBack}
              />
            </div>
          ) : null}
        </>
      ) : null}

      <AppModal
        isOpen={Boolean(deleteTargetTour)}
        onClose={() => {
          if (deletingTour) return;
          setDeleteTargetTour(null);
        }}
        title="Delete Tour"
        size="lg"
      >
        <div className="form-grid">
          <p>Do you want to delete this tour?</p>
          <p>
            <strong>{pickTourName(deleteTargetTour || {})}</strong>
          </p>
          {deleteTourError ? (
            <p className="map-tools-tour-state map-tools-tour-state-error">
              {deleteTourError}
            </p>
          ) : null}
          <div className="table-actions">
            <button
              className="button-danger"
              type="button"
              disabled={deletingTour}
              onClick={handleDeleteTourConfirm}
            >
              {deletingTour ? "Deleting..." : "Delete"}
            </button>
            <button
              className="button-ghost"
              type="button"
              disabled={deletingTour}
              onClick={() => setDeleteTargetTour(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>

      <TourLanguagesModal
        isOpen={isLanguagesModalOpen}
        onClose={() => {
          setIsLanguagesModalOpen(false);
          setSelectedTourForLanguages(null);
        }}
        languages={languages}
        selectedLanguages={selectedLanguages}
        tourId={resolveTourId(selectedTourForLanguages)}
        tourName={pickTourName(selectedTourForLanguages || {})}
        onSave={(payload) => {
          dispatch(setTourSelectedLanguages(payload?.languages || []));
          setIsLanguagesModalOpen(false);
          setSelectedTourForLanguages(null);
        }}
      />
    </div>
  );
}

export default MapOverlayPanel;
